import { ListsEditor } from '@prezly/slate-lists'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import { Editor, Element, Node, Path, Range, Transforms } from 'slate'

const LINE_BREAK_BLOCKS: string[] = [EditorElementTypes.PARAGRAPH, EditorElementTypes.BLOCK_QUOTE]

const HEADINGS: string[] = [
  EditorElementTypes.HEADING_ONE,
  EditorElementTypes.HEADING_TWO,
  EditorElementTypes.HEADING_THREE,
  EditorElementTypes.HEADING_FOUR,
  EditorElementTypes.HEADING_FIVE,
  EditorElementTypes.HEADING_SIX,
]

/** The text of the block from its start up to the caret. */
export const textBeforeCaret = (editor: Editor, path: Path) =>
  Editor.string(editor, { anchor: Editor.start(editor, path), focus: editor.selection.anchor })

/**
 * Line breaks as the saved markdown has them (L1/L2 in
 * project-plans/active/2026-10-30-markdown-editor-parity):
 *
 * - Shift+Enter is a line break (`\n`) inside the paragraph.
 * - Shift+Enter on an empty line ends the paragraph: the empty line becomes the paragraph break.
 * - Shift+Enter in an empty paragraph, or at its start, does nothing; markdown has no empty
 *   paragraphs, so the editor doesn't either.
 * - Inside a list it is Enter: a new item, or out of the list from an empty item. Inside a code
 *   block it is always a line break.
 * - Two empty paragraphs in a row (Enter, Enter, …) collapse into one.
 */
export function withLineBreaks(editor, options: any = {}) {
  const { insertBreak, insertSoftBreak, normalizeNode } = editor

  // Enter at the end of a heading starts a paragraph, not another heading
  editor.insertBreak = () => {
    const { selection } = editor
    const block =
      selection && Editor.above(editor, { match: n => Element.isElement(n) && Editor.isBlock(editor, n) })
    const atHeadingEnd =
      block &&
      HEADINGS.includes((block[0] as any).type) &&
      Range.isCollapsed(selection) &&
      Editor.isEnd(editor, selection.anchor, block[1])
    insertBreak()
    if (atHeadingEnd) {
      Transforms.setNodes(editor, { type: EditorElementTypes.PARAGRAPH } as Partial<Element>)
    }
  }

  editor.insertSoftBreak = () => {
    const { selection } = editor
    if (!selection || !Range.isCollapsed(selection)) {
      return insertSoftBreak()
    }
    const block = Editor.above(editor, { match: n => Element.isElement(n) && Editor.isBlock(editor, n) })
    const type = (block?.[0] as any)?.type

    if (type === EditorElementTypes.CODE_BLOCK) {
      return editor.insertText('\n')
    }
    if (ListsEditor.getListItems(editor).length > 0) {
      // In a list it is Enter: a new item, or out of the list from an empty one. prezly does
      // this on the Enter keydown, which a Shift+Enter never reaches
      Editor.withoutNormalizing(editor, () =>
        ListsEditor.isAtEmptyListItem(editor)
          ? ListsEditor.decreaseDepth(editor)
          : ListsEditor.splitListItem(editor)
      )
      Editor.normalize(editor, { force: true })
      return
    }
    if (!LINE_BREAK_BLOCKS.includes(type)) {
      // Headings: same as Enter
      return editor.insertBreak()
    }

    const before = textBeforeCaret(editor, block[1])
    if (before === '') {
      return
    }
    if (before.endsWith('\n')) {
      // Second Shift+Enter: drop the empty line and start a new paragraph
      Transforms.delete(editor, { distance: 1, unit: 'character', reverse: true })
      return editor.insertBreak()
    }
    editor.insertText('\n')
  }

  editor.normalizeNode = entry => {
    const [node, path] = entry
    if (
      Element.isElementType(node, EditorElementTypes.PARAGRAPH) &&
      Editor.isEmpty(editor, node as Element) &&
      path.length > 0 &&
      path[path.length - 1] > 0
    ) {
      const previous = Node.get(editor, Path.previous(path))
      if (
        Element.isElementType(previous, EditorElementTypes.PARAGRAPH) &&
        Editor.isEmpty(editor, previous as Element)
      ) {
        Transforms.removeNodes(editor, { at: path })
        return
      }
    }
    normalizeNode(entry)
  }

  return editor
}

/**
 * For a shortcut typed at the start of a line that isn't the block's first line (`Intro:⇧⏎- `):
 * moves that line into a block of its own, so the shortcut converts only it. `line` is the
 * shortcut text before the caret; it is deleted.
 */
export const splitOffCurrentLine = (editor: Editor, line: string, before: string) => {
  Transforms.delete(editor, { distance: line.length, unit: 'character', reverse: true })
  if (before.length > line.length) {
    // Remove the `\n` that started this line, then split the block there
    Transforms.delete(editor, { distance: 1, unit: 'character', reverse: true })
    Transforms.splitNodes(editor, { always: true })
  }
}
